(() => {
  const versionKey = '__eurusPaginationStableV1';
  if (window[versionKey]) return;
  window[versionKey] = true;

  const gridRequests = new WeakMap();
  let registered = false;

  function register() {
    if (registered || !window.Alpine) return;
    registered = true;
    window.Eurus?.loadedScript?.add('pagination.js');

    window.Alpine.data('xPagination', sectionId => {
      let root;
      let observer;
      let controller;
      let frame = 0;
      let disposed = false;
      let requestId = 0;
      let currentPage = 1;
      let totalPages = 1;
      let pageParam = 'page';
      let mode = '';
      let activeGrid;
      const completed = new Set();

      function scope() {
        return root?.closest('.shopify-section') || root?.closest('.x-section-product-grid') || root?.parentElement;
      }

      function grid() {
        return scope()?.querySelector('#items-grid, #blog-grid');
      }

      function urlFor(value) {
        if (!value) return null;
        const url = new URL(value, window.location.href);
        if (url.origin !== window.location.origin) throw new Error('Invalid pagination origin');
        url.hash = '';
        return url;
      }

      function keyFor(url) {
        const normalized = new URL(url.href);
        normalized.searchParams.sort();
        return normalized.href;
      }

      function itemKey(item) {
        const key = item.getAttribute('data-pagination-key');
        if (key) return key;
        if (item.id) return 'id:' + item.id;
        return null;
      }

      function safeHook(callback) {
        try { callback(); } catch (error) {
          console.warn('[pagination] Product integration failed:', error);
        }
      }

      return {
        loading: false,
        error: false,
        stale: false,
        nextUrl: '',

        init() {
          root = this.$el;
          currentPage = Number(root.dataset.currentPage) || 1;
          totalPages = Number(root.dataset.totalPages) || currentPage;
          pageParam = root.dataset.pageParam || 'page';
          mode = root.dataset.paginationMode || '';
          this.nextUrl = root.dataset.nextUrl || '';

          if (mode === 'infinite_scroll' && 'IntersectionObserver' in window) {
            observer = new IntersectionObserver(entries => {
              if (entries.some(entry => entry.isIntersecting)) this.loadData();
            }, { rootMargin: '0px 0px 600px 0px' });
            const sentinel = root.querySelector('[data-pagination-sentinel]');
            if (sentinel) observer.observe(sentinel);
          }
        },

        destroy() {
          disposed = true;
          requestId++;
          observer?.disconnect();
          controller?.abort();
          cancelAnimationFrame(frame);
          if (activeGrid && gridRequests.get(activeGrid) === root) {
            gridRequests.delete(activeGrid);
          }
        },

        hasObserver() {
          return 'IntersectionObserver' in window;
        },

        scheduleNext() {
          cancelAnimationFrame(frame);
          if (mode !== 'infinite_scroll' || this.error || !this.nextUrl || disposed) return;
          frame = requestAnimationFrame(() => {
            if (!root.isConnected || this.loading || document.hidden) return;
            const sentinel = root.querySelector('[data-pagination-sentinel]');
            if (!sentinel || !sentinel.getClientRects().length) return;
            const rect = sentinel.getBoundingClientRect();
            if (rect.top <= window.innerHeight + 600 && rect.bottom >= 0) this.loadData();
          });
        },

        async loadData(retry = false) {
          if (retry && this.stale) {
            window.location.reload();
            return;
          }
          if (disposed || this.loading || !this.nextUrl || !root.isConnected || (this.error && !retry)) return;
          const target = grid();
          if (!target || gridRequests.has(target)) return;

          this.loading = true;
          this.error = false;
          gridRequests.set(target, root);
          activeGrid = target;
          const token = ++requestId;
          const locationAtStart = window.location.href;
          const childrenAtStart = Array.from(target.children);
          const countElement = root.querySelector('[data-pagination-count]');
          const abort = new AbortController();
          controller = abort;
          let timedOut = false;
          const timeout = setTimeout(() => {
            timedOut = true;
            abort.abort();
          }, 20000);

          const isCurrent = () => !disposed && token === requestId &&
            root.isConnected && target.isConnected && grid() === target &&
            window.location.href === locationAtStart &&
            root.querySelector('[data-pagination-count]') === countElement &&
            target.children.length === childrenAtStart.length &&
            childrenAtStart.every((item, index) => target.children[index] === item);

          try {
            const url = urlFor(this.nextUrl);
            const page = Number(url.searchParams.get(pageParam));
            const urlKey = keyFor(url);
            if (!Number.isInteger(page) || page <= currentPage || page > totalPages || completed.has(urlKey)) {
              throw new Error('Pagination did not advance to a new page');
            }

            const response = await fetch(url.href, {
              signal: abort.signal,
              credentials: 'same-origin',
              headers: { Accept: 'text/html' }
            });
            if (!response.ok) throw new Error('Pagination HTTP ' + response.status);
            const text = await response.text();
            if (!isCurrent()) {
              disposed = true;
              this.stale = true;
              this.error = true;
              observer?.disconnect();
              return;
            }

            const html = new DOMParser().parseFromString(text, 'text/html');
            const sourceRoot = html.getElementById('btn-pagination-' + sectionId);
            const sourceScope = sourceRoot?.closest('.shopify-section') ||
              sourceRoot?.closest('.x-section-product-grid') || sourceRoot?.parentElement;
            const sourceGrid = sourceScope?.querySelector('#' + target.id);
            const sourceCount = sourceRoot?.querySelector('[data-pagination-count]');
            if (!sourceRoot || !sourceGrid || !sourceCount || Number(sourceRoot.dataset.currentPage) !== page) {
              throw new Error('Pagination response does not contain the requested grid and page');
            }

            const next = sourceRoot.dataset.nextUrl || '';
            const pages = Number(sourceRoot.dataset.totalPages);
            if (!Number.isInteger(pages) || pages < page) throw new Error('Invalid page count');
            if (next) {
              const nextPage = Number(urlFor(next).searchParams.get(pageParam));
              if (!Number.isInteger(nextPage) || nextPage <= page || nextPage > pages) {
                throw new Error('Invalid next page');
              }
            }

            const seen = new Set(Array.from(target.children).map(itemKey).filter(Boolean));
            const fragment = document.createDocumentFragment();
            const added = [];
            for (const item of Array.from(sourceGrid.children)) {
              const key = itemKey(item);
              if (key && seen.has(key)) continue;
              if (key) seen.add(key);
              const imported = document.importNode(item, true);
              added.push(imported);
              fragment.appendChild(imported);
            }
            if (!added.length) throw new Error('Pagination returned no new items');
            if (!isCurrent()) {
              disposed = true;
              this.stale = true;
              this.error = true;
              observer?.disconnect();
              return;
            }

            target.appendChild(fragment);
            completed.add(urlKey);
            currentPage = page;
            totalPages = pages;
            root.dataset.currentPage = String(page);
            root.dataset.totalPages = String(pages);
            root.dataset.nextUrl = next;
            countElement.textContent = sourceCount.textContent;
            this.nextUrl = next;
            if (!next) observer?.disconnect();

            await this.$nextTick();
            if (disposed || !root.isConnected || grid() !== target || window.location.href !== locationAtStart) return;

            safeHook(() => window.SPR?.initDomEls?.());
            safeHook(() => window.Shopify?.PaymentButton?.init?.());
            safeHook(() => window.lazySizes?.autoSizer?.checkElems?.());
            if (target.dataset.paginationCardMode !== 'alpine') {
              safeHook(() => window.ProductCard?.init?.());
              safeHook(() => window.theme?.reinitProductGridItem?.());
            }
            for (const element of added) {
              if (!element.classList.contains('grid-item')) continue;
              document.dispatchEvent(new CustomEvent('products:added', {
                detail: { element }
              }));
            }
          } catch (error) {
            if (!disposed && token === requestId && root.isConnected) {
              this.error = true;
              console.error('[pagination]', timedOut ? 'Request timed out' : error);
            }
          } finally {
            clearTimeout(timeout);
            if (gridRequests.get(target) === root) gridRequests.delete(target);
            if (controller === abort) controller = null;
            if (token === requestId) {
              this.loading = false;
              if (!disposed) this.scheduleNext();
            }
          }
        }
      };
    });
  }

  document.addEventListener('alpine:init', register, { once: true });
  register();
})();
